import axios from 'axios';

import { getApiHost } from '../utils/getApiHost';
import type { SubscriptionStatus } from '../hooks/useSubscriptionStatus';

/** Raw payload from `/user/subscription-status` (before UI transforms). */
export type RawSubscriptionStatus = SubscriptionStatus & {
  current_period_end?: number;
};

const TTL_MS = 60_000;

let cached: RawSubscriptionStatus | null = null;
let cachedUid: string | null = null;
let cachedAt = 0;
let inFlight: Promise<RawSubscriptionStatus> | null = null;
let inFlightUid: string | null = null;
/** Bumped when the cache is dropped so a late response cannot refill it. */
let generation = 0;

type Listener = (
  data: RawSubscriptionStatus | null,
  uid: string | null
) => void;
const listeners = new Set<Listener>();

const emit = () => {
  listeners.forEach((listener) => listener(cached, cachedUid));
};

/**
 * Subscribe to cache updates. Fires whenever a fetch resolves or the cache is
 * invalidated, so passive readers (e.g. the NavBar badge) stay in sync with the
 * page that triggered the change. Returns an unsubscribe function.
 */
export const subscribeSubscriptionStatus = (
  listener: Listener
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const fetchFromApi = async (token: string): Promise<RawSubscriptionStatus> => {
  const apiUrl = getApiHost();
  const headers = { Authorization: `Bearer ${token}` };

  // Retry once per second on 404 to ride out the user-creation race.
  let retries = 3;
  while (retries > 0) {
    try {
      const res = await axios.get(`${apiUrl}/user/subscription-status`, {
        headers,
      });
      return res.data as RawSubscriptionStatus;
    } catch (err: unknown) {
      const axiosError = err as { response?: { status?: number } };
      if (axiosError.response?.status === 404 && retries > 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        retries--;
        continue;
      }
      throw err;
    }
  }
  throw new Error('Failed to fetch subscription status after retries');
};

/**
 * Shared, deduplicated read of `/user/subscription-status`. Concurrent callers
 * for the same account reuse a single in-flight request, and reads within
 * `TTL_MS` reuse that account's cached result. The cache is keyed by `uid`, so
 * signing in as someone else never receives the previous account. Pass `force`
 * to bypass the cache (e.g. after a checkout is fulfilled). Callers must treat
 * the result as read-only.
 */
export const getSubscriptionStatus = (
  token: string,
  uid: string,
  { force = false }: { force?: boolean } = {}
): Promise<RawSubscriptionStatus> => {
  const isFresh =
    cachedUid === uid && !!cached && Date.now() - cachedAt < TTL_MS;
  if (!force && isFresh && cached) return Promise.resolve(cached);
  if (!force && inFlight && inFlightUid === uid) return inFlight;

  const gen = ++generation;
  inFlightUid = uid;
  const request = fetchFromApi(token)
    .then((data) => {
      if (gen !== generation) return data;
      cached = data;
      cachedUid = uid;
      cachedAt = Date.now();
      emit();
      return data;
    })
    .finally(() => {
      if (gen === generation && inFlight === request) {
        inFlight = null;
        inFlightUid = null;
      }
    });
  inFlight = request;
  return request;
};

/**
 * Drop the cache so the next read refetches. Call on sign-out and after
 * mutating a plan. A response already in flight cannot refill the cache.
 */
export const invalidateSubscriptionStatus = (): void => {
  cached = null;
  cachedUid = null;
  cachedAt = 0;
  inFlight = null;
  inFlightUid = null;
  generation += 1;
  emit();
};
